import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const TAB_PATHS = ["cycle-overview", "my-decisions", "budget-reviews"] as const;
type TabPath = (typeof TAB_PATHS)[number];

export const useCycleDetailsTabs = (args: {
	cycleId: string | undefined;
	isAccessLoading: boolean;
	compensationEnabled: boolean | undefined;
	userCanViewCompensation: boolean;
	canLoadMyDecisions: boolean;
	hasCreateCyclePermission: boolean;
	canMakeDecisions: boolean;
	isLoadingMyDecisionsData: boolean;
	isLoadingCycleDetails: boolean;
	cycleDetails: { status?: string; displayStatus?: string } | undefined;
	hasMyDecisionsTab: boolean;
}) => {
	const {
		cycleId,
		isAccessLoading,
		compensationEnabled,
		userCanViewCompensation,
		canLoadMyDecisions,
		hasCreateCyclePermission,
		canMakeDecisions,
		isLoadingMyDecisionsData,
		isLoadingCycleDetails,
		cycleDetails,
		hasMyDecisionsTab,
	} = args;

	const location = useLocation();
	const navigate = useNavigate();

	const lastSegment = location.pathname.split("/").pop() ?? "";
	const pathTab: TabPath | null =
		lastSegment && TAB_PATHS.includes(lastSegment as TabPath) ? (lastSegment as TabPath) : null;

	const hasCanMakeDecisionsPermission = Boolean(hasMyDecisionsTab) && canMakeDecisions;

	const activeTab: TabPath = (() => {
		if (pathTab === "my-decisions") {
			if (hasMyDecisionsTab) {
				return "my-decisions";
			}
			return hasCreateCyclePermission ? "cycle-overview" : "my-decisions";
		}
		if (pathTab === "cycle-overview" || pathTab === "budget-reviews") {
			if (!hasCreateCyclePermission) {
				return "my-decisions";
			}
			return pathTab;
		}
		if (hasCanMakeDecisionsPermission) {
			return "my-decisions";
		}
		return hasCreateCyclePermission ? "cycle-overview" : "my-decisions";
	})();

	useEffect(() => {
		if (isAccessLoading || !cycleId || compensationEnabled === false) {
			return;
		}
		if (!userCanViewCompensation) {
			return;
		}

		// If user can't make decisions but can create cycles, redirect to cycle-overview
		// without waiting for myDecisionsData (which won't load) — but only from root
		// or non-cycle tabs (allow budget-reviews).
		if (!canLoadMyDecisions && hasCreateCyclePermission) {
			const cyclePathPrefix = `/compensation/cycles/${cycleId}`;
			const isAtRoot = location.pathname === cyclePathPrefix || location.pathname === `${cyclePathPrefix}/`;
			if (isAtRoot) {
				navigate("cycle-overview", { replace: true });
			}
			return;
		}

		if (isLoadingMyDecisionsData || !cycleDetails || isLoadingCycleDetails) {
			return;
		}

		const cyclePathPrefix = `/compensation/cycles/${cycleId}`;
		const isAtRoot = location.pathname === cyclePathPrefix || location.pathname === `${cyclePathPrefix}/`;

		if (isAtRoot || lastSegment !== activeTab) {
			navigate(activeTab, { replace: true });
		}
	}, [
		activeTab,
		canLoadMyDecisions,
		compensationEnabled,
		cycleId,
		cycleDetails,
		hasCreateCyclePermission,
		isAccessLoading,
		isLoadingCycleDetails,
		isLoadingMyDecisionsData,
		lastSegment,
		location.pathname,
		navigate,
		userCanViewCompensation,
	]);

	const handleTabChange = (value: string) => {
		if (TAB_PATHS.includes(value as TabPath)) {
			navigate(value);
		}
	};

	const tabItems = [
		...(hasCreateCyclePermission
			? ([
					{
						value: "cycle-overview" as const,
						label: "Cycle Overview",
						content: null,
					},
				] as const)
			: []),
		...(hasMyDecisionsTab
			? ([
					{
						value: "my-decisions" as const,
						label: "My Decisions",
						content: null,
					},
				] as const)
			: []),
		...(hasCreateCyclePermission
			? ([
					{
						value: "budget-reviews" as const,
						label: "Budget Reviews",
						content: null,
					},
				] as const)
			: []),
	];

	return { activeTab, handleTabChange, tabItems };
};
